/**
 * Utility functions for detecting PWA/bookmark status
 */

/**
 * Checks if the app is running in standalone mode (added to home screen)
 * @returns {boolean} true if running as standalone PWA, false if in browser
 */
export const isStandalone = () => {
    // Method 1: Check display-mode media query (most reliable)
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        return true;
    }
    
    // Method 2: Check for navigator.standalone (iOS Safari specific)
    if (window.navigator && window.navigator.standalone === true) {
        return true;
    }
    
    // Method 3: Check if running in app-like environment
    // Some Android browsers set window.matchMedia('(display-mode: standalone)') correctly
    if (window.matchMedia && window.matchMedia('(display-mode: minimal-ui)').matches) {
        return true;
    }
    
    return false;
};

/**
 * Checks if the app is running in a browser (not standalone)
 * @returns {boolean} true if running in browser, false if standalone
 */
export const isBrowser = () => {
    return !isStandalone();
};

/**
 * Gets the current display mode
 * @returns {string} 'standalone', 'fullscreen', 'minimal-ui', or 'browser'
 */
export const getDisplayMode = () => {
    if (window.matchMedia && window.matchMedia('(display-mode: fullscreen)').matches) {
        return 'fullscreen';
    }
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        return 'standalone';
    }
    if (window.matchMedia && window.matchMedia('(display-mode: minimal-ui)').matches) {
        return 'minimal-ui';
    }
    return 'browser';
};

/**
 * Checks if the device supports adding to home screen
 * @returns {boolean} true if supported, false otherwise
 */
export const supportsAddToHomeScreen = () => {
    // Check if it's iOS Safari
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // Check if it's Android Chrome
    const isAndroid = /Android/.test(navigator.userAgent);
    const isChrome = /Chrome/.test(navigator.userAgent);
    
    return (isIOS && isSafari) || (isAndroid && isChrome);
};

/**
 * Hook to listen for display mode changes
 * @param {Function} callback - Function to call when display mode changes
 */
export const addDisplayModeListener = (callback) => {
    if (window.matchMedia) {
        const standaloneQuery = window.matchMedia('(display-mode: standalone)');
        const browserQuery = window.matchMedia('(display-mode: browser)');
        
        const handleChange = () => {
            callback(getDisplayMode());
        };
        
        standaloneQuery.addListener(handleChange);
        browserQuery.addListener(handleChange);
        
        // Return cleanup function
        return () => {
            standaloneQuery.removeListener(handleChange);
            browserQuery.removeListener(handleChange);
        };
    }
    
    return () => {}; // No-op cleanup function
};